/**
 * usePermissions — RBAC permission check hook
 *
 * Reads the current user's permissions from the hydrated auth session.
 * Usage:
 *   const { can } = usePermissions();
 *   if (can('transactions.create')) { ... }
 *
 * Permission keys match the backend's permission registry exactly
 * (see apps/api/src/lib/permissions.ts).
 */

import { useAuthStore } from './useAuth.js';

export function usePermissions() {
  const permissions = useAuthStore((s) => s.permissions);

  /** Check a single permission key */
  function can(permission: string): boolean {
    return permissions.includes(permission);
  }

  /** Check any of the given permissions */
  function canAny(...perms: string[]): boolean {
    return perms.some((p) => permissions.includes(p));
  }

  /** Check all of the given permissions */
  function canAll(...perms: string[]): boolean {
    return perms.every((p) => permissions.includes(p));
  }

  return { permissions, can, canAny, canAll };
}
